<template>
    <div v-if="rt.headers" class="rich-table-container">
        <div v-if="label" class="label">
            {{ label === '_' ? '\u00A0' : label }}
        </div>

        <div class="rich-table-table">
            <table :class="{ 'rich-table': true, 'fixed-header': rt.fixedHeader }">
                <thead class="ignore-all-locks">
                    <tr v-if="hasSlot('fimenu-header')">
                        <td colspan="100" class="sidemenu-contents-header">
                            <slot name="fimenu-header"></slot>
                        </td>
                    </tr>

                    <tr v-if="(rt.paging || rt.search) && !hidePaging">
                        <td colspan="100">
                            <div class="rich-table-paging-container">
                                <div class="rich-table-header-group">
                                    <!--RichTable Actions -->
                                    <div v-if="filtersEnabled || settingsEnabled" class="rich-table-button-group">
                                        <button v-if="filtersEnabled" title="Reset Filters"
                                            class="button-primary reset-btn" @click="resetAllFilters">
                                            <i class="fas fa-undo-alt fa-inverse"></i>
                                        </button>

                                        <button v-if="settingsEnabled" title="Open Settings" class="button-primary"
                                            @click="openSettingsModal">
                                            <i class="fas fa-cog fa-inverse"></i>
                                        </button>
                                    </div>

                                    <!--Paging-->
                                    <div class="rich-table-paging"
                                        :style="$grid(isSelectable ? '1-2-4-2-1' : '1.5-2-4-0.5')" v-if="rt.paging">
                                        <div class="total-rows no-margin-grid" :style="$grid('2-1')">
                                            <span>Results:</span>
                                            <span class="text-bold">{{ rt.count }}</span>
                                        </div>

                                        <div class="page-size no-margin-grid" :style="$grid('2-1')">
                                            <span>Page Size:</span>
                                            <span>
                                                <select v-model="rt.pageSize" @change="
                                                    rt.pageNumber = 1;
                                                refresh();
                                                ">
                                                    <option :value="10">10</option>
                                                    <option :value="25">25</option>
                                                    <option :value="50">50</option>
                                                    <option :value="100">100</option>
                                                </select>
                                            </span>
                                        </div>

                                        <fieldset class="paging-buttons no-margin-grid" :disabled="isBusy"
                                            :style="$grid('1-1-2-1-1')">
                                            <div class="paging-button">
                                                <button :disabled="rt.pageNumber == 1" class="button-clear"
                                                    @click="goToPage(1)">
                                                    <i class="fas fa-step-backward" />
                                                </button>
                                            </div>
                                            <div class="paging-button">
                                                <button :disabled="rt.pageNumber == 1" class="button-clear"
                                                    @click="goToPage(Math.max(rt.pageNumber - 1, 1))">
                                                    <i class="fas fa-angle-left" />
                                                </button>
                                            </div>

                                            <div class="paging-button paging-input">
                                                <input v-model="rt.pageNumber"
                                                    @blur="goToPage(Math.max(Math.min(rt.pageNumber, pageCount), 1))" />
                                                <div>/</div>
                                                <span class="page-count">{{ pageCount }}</span>
                                            </div>

                                            <div class="paging-button">
                                                <button :disabled="rt.pageNumber == pageCount" class="button-clear"
                                                    @click="goToPage(Math.min(rt.pageNumber + 1, pageCount))">
                                                    <i class="fas fa-angle-right" />
                                                </button>
                                            </div>
                                            <div class="paging-button">
                                                <button :disabled="rt.pageNumber == pageCount" class="button-clear"
                                                    @click="goToPage(pageCount)">
                                                    <i class="fas fa-step-forward" />
                                                </button>
                                            </div>
                                        </fieldset>

                                        <div class="total-rows no-margin-grid" v-if="isSelectable"
                                            :style="$grid('2-1')">
                                            <span>Selected Items:</span>
                                            <span class="text-bold">{{ selectedCheckbox.length }}</span>
                                        </div>

                                        <div class="is-busy" v-show="isBusy">
                                            <span><i class="fas fa-circle-notch fa-spin"></i></span>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="search">
                                    <div v-if="serverSide" :class="{
                                        'search-component': true,
                                        'with-search-by': searchKeys && searchKeys.length > 0,
                                    }">
                                        <InputRichDropdown v-if="searchKeys && searchKeys.length > 0" :height="35"
                                            v-model:saturn="rt.searchBy"
                                            :list="[{ key: 'all', label: 'All' }, ...searchKeys]" :valueMap="d => d.key"
                                            :display="d => d.label" search>
                                        </InputRichDropdown>

                                        <InputTextbox :height="35" cssClass="search-input" :showIcon="false"
                                            v-model:saturn="rt.searchText" :clearable="true"
                                            @cleared="resetTextHandler(true)" @keyEnter="() => refresh(true)" />

                                        <button @click="refresh(true)">Search</button>
                                    </div>
                                    <div v-else>
                                        <SearchComponent :data="tableData" v-model:output="tableDataFiltered"
                                            :searchKeys="searchKeys" :instantSearch="instantSearch"
                                            @onSearch="() => (rt.pageNumber = 1)" />
                                    </div>
                                </div>

                                <div v-if="hasSlot('top')" class="header-top">
                                    <slot name="top"></slot>
                                </div>
                            </div>
                        </td>
                    </tr>

                    <!--RichTable Actions w/out Paging-->
                    <tr v-else-if="filtersEnabled || settingsEnabled">
                        <td colspan="100">
                            <div class="rich-table-button-group">
                                <button v-if="filtersEnabled" title="Reset Filters" class="button-primary reset-btn"
                                    @click="resetAllFilters">
                                    <i class="fas fa-undo-alt fa-inverse"></i>
                                </button>

                                <button v-if="settingsEnabled" title="Open Settings" class="button-primary"
                                    @click="openSettingsModal">
                                    <i class="fas fa-cog fa-inverse"></i>
                                </button>
                            </div>
                        </td>
                    </tr>

                    <!--Table Headers-->
                    <tr v-if="!hideHeader">
                        <th v-if="isCollapsable" class="rich-table-header-expandable">
                            <button @click="collapse" class="button-span">
                                <i :class="{
                                    'fas fa-plus': isCollapsed,
                                    'fas fa-minus': !isCollapsed,
                                }" style="color: var(--main-color)" />
                            </button>
                        </th>
                        <th v-if="isSelectable" class="rich-table-header-checkbox">
                            <div @click.prevent="onSelectAllItems" v-if="!isRadio && !hideSelectAll">
                                <InputCheckbox v-model="selectAllCheckbox" :useSquare="useSquareInputCheckbox" />
                            </div>
                        </th>
                        <th v-for="(h, index) in visibleHeaders" :key="`${h.name}-header-${index}`"
                            :class="['rich-table-header', h.cssClass]" @click.stop="onHeaderClicked(h)" ref="headers">
                            <div class="rich-header">
                                <div v-if="h.icon" class="icon-header">
                                    <i :class="h.icon" />
                                </div>
                                <div v-else class="rich-table-header-title">
                                    {{ typeof h.display === 'function' ? h.display(rowItem) : h.display }}
                                </div>

                                <div class="rich-header-options">
                                    <div v-if="h.autoFilterMenuAvailable()" :class="{
                                        'rich-table-auto-filter': true,
                                        active: h.hasSelectedAutoFilter() || h.hasActiveRangeFilter(),
                                    }">
                                        <i class="fas fa-sliders-h" />
                                    </div>
                                    <div v-else-if="h.sortable" class="rich-header-sort">
                                        <div :class="{ active: rt.sortBy === h.name && !rt.asc }">
                                            <i class="fas fa-caret-up"></i>
                                        </div>
                                        <div :class="{ active: rt.sortBy === h.name && rt.asc }">
                                            <i class="fas fa-caret-down"></i>
                                        </div>
                                    </div>
                                </div>

                                <RichTableAutoFilter v-if="h.isOpen" :headerRef="$refs.headers[index]" :header="h"
                                    :sort="sort" :sortBy="rt.sortBy" :asc="rt.asc"
                                    :autoFilterToggleSelectAll="autoFilterToggleSelectAll"
                                    :toggleAutoFilterValue="toggleAutoFilterValue"
                                    :onRangeFilterChanged="onRangeFilterChanged" :onHeaderClicked="onHeaderClicked">
                                </RichTableAutoFilter>
                            </div>

                            <span v-if="h.actions" class="action-cell">
                                <span v-for="ha in h.actions.filter(a => a.header)" :key="`${ha.name}-action`"
                                    class="rich-table-actions">
                                    <button @click="ha.action()" :class="ha.cssClass" v-if="ha.type === 'button'">
                                        <span v-if="ha.icon"><i
                                                :class="ha.icon.trim().indexOf(' ') != -1 ? ha.icon : 'fas ' + ha.icon"></i></span>
                                        <span v-if="ha.text">{{ util.isFunction(ha.text) ? ha.text() : ha.text }}</span>
                                    </button>
                                    <a href="#" @click.prevent="ha.action()" :class="ha.cssClass"
                                        v-if="ha.type === 'link'">
                                        <span v-if="ha.text">{{ util.isFunction(ha.text) ? ha.text() : ha.text }}</span>
                                    </a>
                                </span>
                            </span>
                        </th>
                    </tr>
                </thead>

                <tbody :class="{ 'rich-table-body': true, collapsed: isCollapsed }"
                    :style="noUppercase ? 'text-transform: none !important' : ''">
                    <tr v-if="noData && rt.data.length <= 0" class="no-data-message">
                        <td :colspan="visibleHeaders.length">{{ noData }}</td>
                    </tr>

                    <tr v-for="(d, index) in rt.data" :key="`row-${index}`" :class="tableRowStyle(d)"
                        @click="onRowClick(d)">
                        <td v-if="isCollapsable" />

                        <td v-if="isSelectable" class="rich-table-rows-checkbox">
                            <InputRadio v-if="isRadio" :disabled="isSelectDisabled(d)" ref="radioBox" :valueMatch="d"
                                v-model="selectedCheckbox[0]"></InputRadio>
                            <InputCheckbox v-else :isControlled="true" @click="onSelectItem(d)"
                                :disabled="isSelectDisabled(d)" ref="checkBox"
                                :modelValue="selectedCheckbox.some(sRow => selectionKey(sRow) == selectionKey(d))"
                                :useSquare="useSquareInputCheckbox" />
                        </td>

                        <td v-for="(h, hIndex) in visibleHeaders" :key="`${h.name}-cell-${hIndex}`"
                            class="rich-table-rows" :class="h.getCellCssClass(d)">
                            <div v-if="h.subHeaders">
                                <RichTableDataCell v-for="(sh, jndex) in h.subHeaders" :key="`${sh.name}-cell-${jndex}`"
                                    :class="sh.getCellCssClass(d)" :tableData="tableData" :rowIndex="index" :header="sh"
                                    :row="d">
                                    <template v-if="sh.slot" #[sh.slot]="row">
                                        <slot :name="sh.slot" v-bind="d"></slot>
                                    </template>
                                </RichTableDataCell>
                            </div>

                            <RichTableDataCell v-else :tableData="tableData" :rowIndex="index" :header="h" :row="d">
                                <template v-if="h.slot" #[h.slot]="row">
                                    <slot :name="h.slot" v-bind="d"></slot>
                                </template>
                            </RichTableDataCell>
                        </td>
                    </tr>

                    <tr v-if="anyFooters" class="rich-table-footer">
                        <th v-for="(h, index) in visibleHeaders" :key="`${h.name}-footer-${index}`" :class="h.cssClass">
                            {{ h.footer ? h.footer() : '' }}
                        </th>
                    </tr>

                    <tr v-if="rt.pageNumber === pageCount && entriesNotIncluded > 0">
                        <th colspan="100" style="text-transform: none">
                            <p>
                                Not showing
                                {{ util.formatUSNumbers(entriesNotIncluded) }} entries. To see more specific results,
                                try applying more
                                filters.
                            </p>
                        </th>
                    </tr>
                </tbody>
            </table>

            <div v-if="hasSlot('footer')" class="header-footer">
                <slot name="footer"></slot>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'underscore';
import util from '@core/services/util';
import $modal from '@core/services/modal';
import RichTableHeader from '@core/classes/RichTableHeader';
import { RichTableServerSide } from '@core/classes/RichTableServerSide';
import InputRadio from '@core/components/InputRadio.vue';
import InputTextbox from '@core/components/InputTextbox.vue';
import InputCheckbox from '@core/components/InputCheckbox.vue';
import InputRichDropdown from '@core/components/InputRichDropdown.vue';
import SearchComponent from '@core/components/SearchComponent.vue';
import RichTableDataCell from '@core/components/RichTableDataCell.vue';
import RichTableAutoFilter from '@core/components/RichTableAutoFilter.vue';
import modalRichTableSettings from '@core/modals/modalRichTableSettings.vue';

export default {
    name: 'RichTable',
    emits: ['selectionChanged', 'row-click'],
    props: {
        label: String,
        serverSide: [Object, RichTableServerSide],
        tableData: Array,
        headers: Array,
        additionalHeaders: Array,
        headerGroups: Array, // Format: { name: '...', display: '...', cssClass: '...', subHeaderNames: []}, will stack data associated with listed headers
        fixedHeader: {
            type: Boolean,
            default: () => false,
        },
        hideHeader: Boolean,
        paging: {
            type: Boolean,
            default: () => false,
        },
        hidePaging: Boolean,
        allowDeepPagination: {
            // Only applicable for serverside tables where you may run into ElasticSearch's 10,000 document limit.
            type: Boolean,
            default: () => false,
        },
        selectable: String, // Supported Values: 'checkbox', 'radio'
        selectionKey: Function,
        isSelectedKey: Function,
        hideSelectAll: Boolean,
        search: {
            type: Boolean,
            default: () => false,
        },
        additionalSearchKeys: {
            // To search for data not necessarily tied to a column header
            type: Array,
            default: () => [],
        },
        noUppercase: Boolean,
        isBusy: Boolean,
        noData: String,
        settingsEnabled: Boolean,
        downloadRoute: Function, // If using serverside data, provide an async function that calls the right download route to download richtable data.
        tableId: String, // Unique identifier for table, pass in if you want table settings to be saved to local storage
        pdfTitle: String, // Title of report when exporting to PDF (optional)
        initialPageSize: Number,
        instantSearch: Boolean,
        useSquareInputCheckbox: {
            // Whether or not use the square UI for the input checkbox.
            required: false,
            type: Boolean,
            default: false,
        },
        isSelectDisabled: {
            required: false,
            type: Function,
            default: () => false,
        },
        isCollapsable: {
            required: false,
            type: Boolean,
            default: false,
        },
        rowClick: {
            required: false,
            type: Function,
            default: () => null,
        },
        tableRowStyle: {
            required: false,
            type: Function,
            default: () => '',
        },
        showResetFiltersButton: {
            required: false,
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            rt: {
                data: null,
                headers: null,
                sortBy: '',
                asc: true,
                fixedHeader: this.fixedHeader,
                paging: null,
                search: null,
                pageNumber: 1,
                pageSize: 10,
                count: null,
                icon: null,
                nonObject: null,
                searchText: '',
                searchBy: 'all',
                searchAfterList: [], // Only used for serverside
            },
            filters: [],
            rangeFilters: [],
            initialData: null,
            selectedCheckbox: [],
            selectAllCheckbox: false,
            initialSorted: null,
            tableDataFiltered: null,
            searchKeys: [],
            isCollapsed: false,
            tBody: null,
        };
    },
    computed: {
        anyFooters() {
            return _.some(this.rt.headers, h => !!h.footer);
        },
        pageCount() {
            if (!this.serverSide) return Math.ceil(this.rt.count / this.rt.pageSize);

            const totalPages = Math.ceil(this.serverSide.total / this.rt.pageSize);
            if (this.allowDeepPagination) return totalPages;

            // ElasticSearch does not allow you to page past 10,000 documents
            const pageLimit = Math.floor(10000 / this.rt.pageSize);
            return Math.min(totalPages, pageLimit);
        },
        entriesNotIncluded() {
            if (!this.serverSide || this.allowDeepPagination) return 0;
            const entriesReturned = this.pageCount * this.rt.pageSize;
            return this.serverSide.total - entriesReturned;
        },
        util() {
            return util;
        },
        isSelectable() {
            return this.selectable === 'checkbox' || this.selectable === 'radio';
        },
        isRadio() {
            return this.selectable === 'radio';
        },
        visibleHeaders() {
            return this.rt.headers.filter(h => h.isVisible);
        },
        filtersEnabled() {
            const headerHasFilter = this.rt.headers.some(h => h.autoFilter || h.rangeFilter);
            const subHeaderHasFilter = this.rt.headers.some(h => h.subHeaders?.some(sh => sh.autoFilter || sh.rangeFilter));

            return this.showResetFiltersButton && (this.search || headerHasFilter || subHeaderHasFilter);
        },
    },
    created() {
        this.tableDataFiltered = this.tableData;
        this.setupHeaders();
        this.rt.paging = this.paging;
        this.rt.search = this.search;
        if (this.initialPageSize != null) this.rt.pageSize = this.initialPageSize;
        //LOAD IN PREFERENCES FROM LOCAL STORAGE
        const tablePreferences = JSON.parse(localStorage.getItem('RichTablePreferences') ?? '[]');
        const preferenceIndex = tablePreferences?.findIndex(p => p.tableId === this.tableId);

        if (preferenceIndex != -1) {
            const preferenceItem = tablePreferences[preferenceIndex];

            this.rt.headers.forEach(h => {
                const headerMatch = preferenceItem.headers.find(p => p.name === h.name);
                if (headerMatch) {
                    h.isVisible = headerMatch.isVisible;
                }
            });
        }

        this.init();

        if (this.isSelectedKey) this.selectedCheckbox = this.rt.data.filter(r => this.isSelectedKey(r));

        if (!this.hideSelectAll) this.setSelectAllCheckbox();

        if (!util.isNull(this.initialSorted)) this.sort(this.initialSorted, this.initialSorted.asc ?? true);

        //WATCH IF THE DATA CHANGED FROM OUTSIDE
        this.$watch(
            () => this.$props.tableData,
            newValue => {
                this.tableDataFiltered = newValue;

                if (!this.serverSide) util.debounce(this.setupHeaders, 100);
                util.debounce(this.init, 100);
            },
        );

        this.$watch(
            () => this.tableDataFiltered,
            () => {
                util.debounce(this.init, 100);
            },
            { deep: true },
        );

        if (this.selectable == 'radio') {
            this.$watch(
                () => this.selectedCheckbox[0],
                (newValue, oldValue) => {
                    if (newValue !== oldValue) {
                        this.$emit('selectionChanged', newValue);
                    }
                },
                { deep: true, immediate: true },
            );
        }

        //TO CLOSE ANY HEADERS WHEN CLICKING OUTSIDE
        this.EventBus.on('bodyClicked', this.onBodyClicked);
    },
    beforeUnmount() {
        this.EventBus.off('bodyClicked', this.onBodyClicked);
    },
    methods: {
        onRowClick(row) {
            if (this.rowClick) {
                this.$emit('row-click', row);
            }
        },
        collapse() {
            this.isCollapsed = !this.isCollapsed;
        },
        setupHeaders() {
            this.rt.headers = [];

            if (this.serverSide) {
                this.rt.headers = this.serverSide.headers.map(h => this.convertToRTHeader(h));

                if (this.additionalHeaders) {
                    const convertedExtraHeaders = this.additionalHeaders.map(ah => this.convertToRTHeader(ah));
                    this.rt.headers = _.sortBy(this.rt.headers.concat(convertedExtraHeaders), h => h.columnOrder);
                }
            } else {
                if (this.headers) {
                    this.headers.forEach(h => {
                        const header = this.convertToRTHeader(h);
                        this.rt.headers.push(header);

                        if (h.sorted) this.initialSorted = this.rt.headers[this.rt.headers.length - 1];
                    });
                } else {
                    Object.keys(this.rt.data[0]).forEach(key => {
                        const header = new RichTableHeader({ name: key, display: key });
                        this.rt.headers.push(header);
                    });
                }
            }

            //Generate search keys
            this.searchKeys = [];
            this.rt.headers
                .filter(f => f.searchable)
                .forEach(h => {
                    this.searchKeys.push({
                        key: h.name,
                        label: h.display,
                        value: typeof h.value == 'function' ? h.value : null,
                    });
                });

            if (this.additionalSearchKeys.length > 0) this.searchKeys.push(...this.additionalSearchKeys);

            //Group together stacked headers
            this.headerGroups?.forEach(g => {
                const subHeaders = this.rt.headers.filter(h => !!g.subHeaderNames.find(n => n === h.name));
                const group = new RichTableHeader({ ...g, subHeaders: subHeaders });

                subHeaders.forEach((sh, index) => {
                    sh.isSubHeader = true;

                    //Stacked header takes the column order of the column that appears first
                    const oldIndex = this.rt.headers.findIndex(h => h.name === sh.name);

                    if (index === 0) {
                        group.columnOrder = sh.columnOrder;
                        this.rt.headers.splice(oldIndex, 1, group);
                    } else {
                        this.rt.headers.splice(oldIndex, 1);
                    }
                });
            });
        },
        convertToRTHeader(header) {
            return header instanceof RichTableHeader ? header : new RichTableHeader(header);
        },
        init() {
            if (this.hidePaging) this.rt.pageSize = this.tableData.length;

            //CLONING THE DATA
            if (this.tableDataFiltered && this.tableDataFiltered[0] && typeof this.tableDataFiltered[0] === 'object') {
                this.initialData = this.tableDataFiltered.map((d, index) => {
                    const x = util.clone(d);
                    x._ref = d;
                    x._index = index;
                    return x;
                });
            } else {
                this.initialData =
                    this.tableDataFiltered && this.tableDataFiltered.length > 0
                        ? this.tableDataFiltered.map(d => {
                            return { value: d };
                        })
                        : [];
            }

            this.rt.data = util.clone(this.initialData);

            //SERVERSIDE INIT
            if (this.serverSide) {
                if (this.serverSide.pageNumber === 1) this.rt.pageNumber = this.serverSide.pageNumber;

                this.rt.pageSize = this.serverSide.pageSize;
                this.rt.count = this.serverSide.total;
                this.rt.asc = this.serverSide.asc;
                this.rt.sortBy = this.serverSide.sortBy;

                if (this.serverSide.searchAfter) this.addSearchAfter(this.serverSide.searchAfter);

                //Update autofilter values
                this.serverSide.headers
                    .filter(f => f.autoFilter)
                    .forEach(h => {
                        const rtHeader = this.rt.headers.find(rt => rt.name === h.name);
                        if (rtHeader) {
                            rtHeader.autoFilter.values = h.autoFilter.values.map(x => x);
                        }
                    });
            } else {
                this.rt.count = this.initialData.length;
            }

            //NON-SERVERSIDE INIT
            if (!this.serverSide) {
                //SET UP AUTO FILTER VALUES
                this.rt.headers?.forEach(header => {
                    if (header.subHeaders) return header.subHeaders.forEach(sh => sh.initAutoFilters(this.initialData));
                    return header.initAutoFilters(this.initialData);
                });

                this.refresh();
            }
        },
        async refresh(restartPage = false) {
            if (restartPage) this.rt.pageNumber = 1;

            if (this.serverSide) {
                if (restartPage) {
                    this.rt.searchAfterList = [];
                    this.serverSide.searchAfter = null;
                }

                const serverHeaders = [];
                this.rt.headers.forEach(h => {
                    if (h.subHeaders) serverHeaders.push(...h.subHeaders);
                    else serverHeaders.push(h);
                });

                this.serverSide.headers = serverHeaders;
                this.serverSide.sortBy = this.rt.sortBy;
                this.serverSide.asc = this.rt.asc;
                this.serverSide.pageSize = this.rt.pageSize;
                this.serverSide.pageNumber = this.rt.pageNumber;
                this.serverSide.searchText = this.rt.searchText;
                this.serverSide.searchBy = this.rt.searchBy;

                if (this.serverSide.searchAfter?.pageNumber !== this.rt.pageNumber - 1) {
                    this.serverSide.searchAfter = this.findSearchAfter(this.rt.pageNumber);
                }

                await this.serverSide.refresh();

                // Range filters may have been swapped serverside
                this.rt.headers
                    .filter(h => h.rangeFilter || h.subHeaders)
                    .forEach(h => {
                        h.subHeaders?.forEach(sh => this.syncRangeFiltersWithServerside(sh));
                        this.syncRangeFiltersWithServerside(h);
                    });

                if (this.serverSide.searchAfter) this.addSearchAfter(this.serverSide.searchAfter);
            } else {
                //Paging
                const c = (this.rt.pageNumber - 1) * this.rt.pageSize;
                const limit = (x, i) => i <= this.rt.pageSize - 1;
                const skip = (x, i) => i > c - 1;

                //Auto-filters
                let filteredData = this.initialData;
                if (this.filters && this.filters.length > 0) {
                    //group by header name
                    const groups = _.groupBy(this.filters, f => f.name.split('|')[0]);

                    // AND every header and OR every item selected in header
                    filteredData = this.initialData.filter(row => Object.keys(groups).every(key => groups[key].some(f => f.func(row))));
                }

                //Range filters
                if (this.rangeFilters && this.rangeFilters.length > 0) {
                    filteredData = filteredData.filter(row => this.rangeFilters.every(f => f.func(row)));
                }

                //Sort by
                const sortedHeader = this.rt.headers.find(h => h.name === this.rt.sortBy);

                let sortedData = _.sortBy(filteredData, val => {
                    if (sortedHeader?.sortValue) return sortedHeader.sortValue(val);
                    return val[this.rt.sortBy];
                });
                if (!this.rt.asc) sortedData = sortedData.reverse();
                if (this.paging) sortedData = sortedData.filter(skip).filter(limit);

                this.rt.count = filteredData.length;
                this.rt.data = sortedData;
            }

            if (!this.hideSelectAll) this.setSelectAllCheckbox();
        },
        findParentHeader(subHeaderName) {
            return this.rt.headers.find(h => {
                if (!h.subHeaders || h.subHeaders.length < 1) return false;
                return h.subHeaders.some(sh => sh.name === subHeaderName);
            });
        },
        sort(header, isAscending = null) {
            //Sorts rt.data

            if (isAscending !== null) this.rt.asc = isAscending;
            else if (this.rt.sortBy === header.name) this.rt.asc = !this.rt.asc;
            else this.rt.asc = this.serverSide ? this.serverSide.asc : true;

            this.rt.sortBy = header.name;
            header.isOpen = false;

            if (header.isSubHeader) {
                let parentHeader = this.findParentHeader(header.name);
                parentHeader.isOpen = false;
            }

            this.refresh(true);
        },
        goToPage(pageNumber) {
            this.rt.pageNumber = pageNumber;
            this.refresh();
        },
        setSelectAllCheckbox() {
            const selectedbles = this.rt.data.filter(rtRow => !this.isSelectDisabled(rtRow));

            const allOptionsChecked = selectedbles.every(rtRow => {
                return this.selectedCheckbox.some(sRow => this.selectionKey(sRow) == this.selectionKey(rtRow));
            });

            this.selectAllCheckbox = allOptionsChecked && selectedbles.length > 0;
        },
        selectAll() {
            const itemsToAdd = this.rt.data.filter(rtRow => {
                if (this.isSelectDisabled && this.isSelectDisabled(rtRow)) return false;

                return this.selectedCheckbox.every(selectedRow => {
                    return this.selectionKey(selectedRow) != this.selectionKey(rtRow);
                });
            });
            this.selectedCheckbox.push(...itemsToAdd);
            this.setSelectAllCheckbox();
            this.$emit('selectionChanged', this.selectedCheckbox);
        },
        unSelectAll() {
            this.selectedCheckbox = this.selectedCheckbox.filter(
                selectedRow => !this.rt.data.some(rtRow => this.selectionKey(rtRow) == this.selectionKey(selectedRow)),
            );
            this.setSelectAllCheckbox();
            this.$emit('selectionChanged', this.selectedCheckbox);
        },
        onSelectAllItems() {
            if (!this.selectAllCheckbox) this.selectAll();
            else this.unSelectAll();
        },
        onSelectItem(row) {
            //select or unselect
            const existingItemIndex = this.selectedCheckbox.findIndex(i => this.selectionKey(i) == this.selectionKey(row));
            if (existingItemIndex > -1) {
                this.selectedCheckbox.splice(existingItemIndex, 1);
            } else {
                this.selectedCheckbox.push(row);
            }

            // this is the all items selected on this page checkbox
            if (!this.hideSelectAll) this.setSelectAllCheckbox();

            this.$emit('selectionChanged', this.selectedCheckbox, row, existingItemIndex > -1 ? 'uncheck' : 'check');
        },
        onBodyClicked() {
            this.closeAllHeaders();
        },
        updateHeader(tHeader, filters, resetFilters) {
            const filterFound = filters.find(filter => filter.name.toLowerCase() == tHeader.name.toLowerCase());
            if (tHeader.autoFilter) {
                tHeader.autoFilter.values.forEach(af => {
                    if (resetFilters) {
                        af.isSelected = false;
                    }
                    if (filterFound) {
                        const filterFoundValue = filterFound.values.find(val => val.value.toLowerCase() == af.value.toLowerCase());
                        if (filterFoundValue) {
                            af.isSelected = filterFoundValue.isSelected;
                        }
                    }
                });
            }
        },
        applyFilterFromOutside(filters, resetFilters = false) {
            this.visibleHeaders.forEach(header => {
                this.updateHeader(header, filters, resetFilters);
                if (header.subHeaders) {
                    header.subHeaders.forEach(sHeader => {
                        this.updateHeader(sHeader, filters, resetFilters);
                    });
                }
            });
            this.refresh(true);
        },
        onHeaderClicked(header) {
            //Opens/closes column header
            this.closeAllHeaders(header);

            if (header.autoFilterMenuAvailable()) {
                header.isOpen = !header.isOpen;
            } else if (header.sortable) {
                this.sort(header);
            }

            if (header.isOpen) header.anythingChanged = false;

            //ONCE CLOSED, RUN THE AUTOFILTER & SORT
            if (!header.isOpen && header.anythingChanged) this.refresh(true);
        },
        closeAllHeaders(except) {
            const openHeader = this.rt.headers.find(h => h.isOpen && h != except);
            if (openHeader) this.onHeaderClicked(openHeader);
        },
        autoFilterToggleSelectAll(header, toggle) {
            header.autoFilter.values.forEach(v => {
                if (v.isSelected != toggle) {
                    this.toggleAutoFilterValue(header, v, toggle);
                }
            });
        },
        toggleAutoFilterValue(header, value, override = null) {
            header.anythingChanged = true;

            if (header.isSubHeader) {
                let parentHeader = this.findParentHeader(header.name);
                parentHeader.anythingChanged = true;
            }

            value.isSelected = override ?? !value.isSelected;
            if (this.serverSide) return;

            if (value.isSelected) {
                const filterFunc =
                    typeof header.rowValue(this.initialData[0]) !== 'object'
                        ? row => header.rowValue(row).toString() === value.value.toString()
                        : row => header.rowValue(row).toString().indexOf(value.value) !== -1;

                header.autoFilter.deselectedValues = header.autoFilter.deselectedValues.filter(v => v !== value.value);

                this.filters.push({
                    name: header.name + '|' + value.value,
                    func: filterFunc,
                });
            } else {
                header.autoFilter.deselectedValues.push(value.value);
                this.filters = this.filters.filter(f => f.name !== header.name + '|' + value.value);
            }
        },
        onRangeFilterChanged(header, min, max) {
            header.anythingChanged = true;
            if (this.serverSide) return;

            const minFilter = util.isNull(min) || min === '' ? '-INF' : min;
            const maxFilter = util.isNull(max) || max === '' ? 'INF' : max;

            //If min/max both don't have a value, remove header from filter list
            if (minFilter === '-INF' && maxFilter === 'INF') {
                this.rangeFilters = this.rangeFilters.filter(f => f.name.indexOf(header.name) === -1);
                return;
            }

            const newRangeFilter = {
                name: `${header.name}|${minFilter}|${maxFilter}`,
                func: row => this.isValueBetweenRange(header.rowValue(row), min, max),
            };

            const existingFilter = this.rangeFilters.findIndex(f => f.name.indexOf(header.name) !== -1);
            if (!util.isNull(existingFilter) && existingFilter !== -1) this.rangeFilters[existingFilter] = newRangeFilter;
            else this.rangeFilters.push(newRangeFilter);
        },
        syncRangeFiltersWithServerside(header) {
            if (!header.rangeFilter) return;

            const serverSideHeader = this.serverSide.headers.find(ssh => ssh.name === header.name);
            if (serverSideHeader) header.rangeFilter = serverSideHeader.rangeFilter;
        },
        isValueBetweenRange(rowValue, min, max) {
            //Assumes that rowValue is a number
            if (util.isNull(rowValue)) return false;

            min ??= Number.NEGATIVE_INFINITY;
            max ??= Number.POSITIVE_INFINITY;
            return rowValue >= min && rowValue <= max;
        },
        hasSlot(name) {
            // return this.$slots[name] !== undefined && this.$slots[name]().some(x => x.children?.length > 0);
            return util.hasKey(this.$slots, name);
        },
        openSettingsModal() {
            $modal.open(modalRichTableSettings, {
                name: 'modalRichTableSettings',
                passedData: {
                    rt: this.rt,
                    tableDataFiltered: this.tableDataFiltered,
                    tableId: this.tableId,
                    downloadRoute: this.downloadRoute,
                    isServerSide: !!this.serverSide,
                    pdfTitle: this.pdfTitle,
                },
            });
        },
        resetTextHandler(triggerRefresh = false) {
            this.rt.searchText = '';
            if (triggerRefresh) this.refresh();
        },
        resetAllFilters() {
            if (this.serverSide) this.resetTextHandler();
            else this.EventBus.emit('SearchComponent_Reset');

            this.rt.sortBy = '';
            this.rt.searchBy = 'all';
            this.rangeFilters = [];

            this.rt.headers
                .filter(f => f.autoFilter || f.rangeFilter)
                .forEach(h => {
                    if (h.autoFilter) {
                        this.autoFilterToggleSelectAll(h, false);
                    }

                    if (h.rangeFilter) {
                        h.rangeFilter.min = null;
                        h.rangeFilter.max = null;
                    }
                });

            this.rt.headers
                .filter(h => h.subHeaders)
                .forEach(head => {
                    head.subHeaders
                        .filter(sh => sh.autoFilter || sh.rangeFilter)
                        .forEach(s => {
                            if (s.autoFilter) {
                                this.autoFilterToggleSelectAll(s, false);
                            }

                            if (s.rangeFilter) {
                                s.rangeFilter.min = null;
                                s.rangeFilter.max = null;
                            }
                        });
                });

            this.refresh(true);
        },
        addSearchAfter(newSearchAfter) {
            if (!this.serverSide) return;

            const currPage = this.rt.pageNumber;
            const searchAfterList = this.rt.searchAfterList;

            if (searchAfterList.length > 25) {
                const toRemove = searchAfterList.reduce((indexToRemove, currValue, currIndex) => {
                    // Find the item furthest away from the current page and remove that.
                    const toRemoveValue = searchAfterList[indexToRemove];
                    const toRemoveDiff = Math.abs(currPage - toRemoveValue.pageNumber);
                    const currValueDiff = Math.abs(currPage - currValue.pageNumber);

                    return toRemoveDiff > currValueDiff ? indexToRemove : currIndex;
                }, 0);

                searchAfterList.splice(toRemove, 1);
            }

            const existingSearchAfter = searchAfterList.find(s => s.pageNumber === newSearchAfter.pageNumber);
            if (existingSearchAfter) existingSearchAfter.searchAfter = newSearchAfter.searchAfter;
            else searchAfterList.push(newSearchAfter);
        },
        findSearchAfter(currPageNumber) {
            if (!this.serverSide) return;
            return this.rt.searchAfterList.find(s => s.pageNumber === currPageNumber - 1);
        },
    },
    components: {
        InputRadio,
        InputTextbox,
        InputCheckbox,
        InputRichDropdown,
        SearchComponent,
        RichTableDataCell,
        RichTableAutoFilter,
    },
};
</script>
<style>
.total-rows {
    display: flex;
    align-items: center;
}

.rich-table-container .input-component-wrapper {
    position: relative;
    left: 6px;
}

.rich-table-container .reset-btn {
    height: 35px;
    position: relative;
    left: 3px;
}

.rich-table-container .rich-table-rows-checkbox .checkbox {
    height: 16px;
    width: 16px;
}

.rich-table-body.collapsed {
    display: none;
}

.rich-table-header-checkbox {
    min-width: 46px;
}

.rich-table-header-expandable>button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 100%;
}

.rich-table-container .paging-buttons.no-margin-grid button {
    box-shadow: none !important;
}
</style>
